/**
 * Credit Service
 * Frontend service for credit-related operations.
 *
 * ⚠️ IMPORTANT: Frontend never deducts credits. Backend is the single source of truth.
 * This service only:
 * - Fetches credit balance (read-only)
 * - Checks if user has sufficient credits (read-only)
 * - Displays credit costs (read-only)
 *
 * Credit rules are defined in /docs/credits.md. Do not redefine them.
 */

import { supabase } from "@/integrations/supabase/client";
import {
  JobType,
  CREDIT_COSTS,
  getCreditCost,
  CheckSufficientCreditsResponse,
  CreditLedgerEntry,
} from "@/types/credits";
import { emitCreditsConsumed } from "@/utils/credits";

// Event target for broadcasting client-side balance updates (instant UI refresh)
export const creditBalanceEvent = new EventTarget();

/**
 * Get user's current credit balance.
 * This is a read-only operation.
 */
export async function getCreditBalance(userId: string): Promise<number> {
  try {
    // Fetch from profiles table directly as credits_balance is stored there
    const { data, error } = await (supabase as any)
      .from("profiles")
      .select("credits_balance")
      .eq("id", userId)
      .single();

    if (error) {
      console.error("Error fetching credit balance:", error);
      return 0;
    }

    return data?.credits_balance ?? 0;
  } catch (error) {
    console.error("Error in getCreditBalance:", error);
    return 0;
  }
}

/**
 * Get user's current credit balances (regular + bonus).
 * Returns object { regular: number, bonus: number }
 */
export async function getCreditBalances(
  userId: string,
): Promise<{ regular: number; bonus: number }> {
  try {
    const { data, error } = await (supabase as any)
      .from("profiles")
      .select("credits_balance, credits_bonus")
      .eq("id", userId)
      .single();

    if (error) {
      console.error("Error fetching credit balances:", error);
      return { regular: 0, bonus: 0 };
    }

    return {
      regular: data?.credits_balance ?? 0,
      bonus: data?.credits_bonus ?? 0,
    };
  } catch (error) {
    console.error("Error in getCreditBalances:", error);
    return { regular: 0, bonus: 0 };
  }
}

/**
 * Check if user has sufficient credits for a job.
 * This is a read-only operation and does not consume credits.
 */
export async function checkSufficientCredits(
  userId: string,
  jobType: JobType,
): Promise<CheckSufficientCreditsResponse> {
  const creditsRequired = getCreditCost(jobType);

  try {
    // Consider both regular and bonus balances for sufficiency checks
    const { regular, bonus } = await getCreditBalances(userId);
    const total = (regular ?? 0) + (bonus ?? 0);
    const sufficient = total >= creditsRequired;
    const shortfall = Math.max(0, creditsRequired - total);
    const willUseBase =
      (bonus ?? 0) < creditsRequired && total >= creditsRequired;

    return {
      sufficient,
      currentBalance: total,
      creditsRequired,
      shortfall,
      // extended fields for callers that can show breakdowns
      regular,
      bonus,
      willUseBase,
    } as unknown as CheckSufficientCreditsResponse;
  } catch (error) {
    console.error("Error checking credit sufficiency:", error);
    // On error, assume insufficient to prevent accidental execution
    return {
      sufficient: false,
      currentBalance: 0,
      creditsRequired,
      shortfall: creditsRequired,
    } as CheckSufficientCreditsResponse;
  }
}

/**
 * Get credit transaction history for a user.
 * This is a read-only operation.
 */
export async function getCreditLedger(
  userId: string,
  limit: number = 50,
): Promise<CreditLedgerEntry[]> {
  try {
    // Query credit_ledger table - use any to bypass type checking
    // since this table may not be in the generated types yet
    const { data, error } = await (supabase as any)
      .from("credit_ledger")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error fetching credit ledger:", error);
      return [];
    }

    return (data || []).map((entry: any) => ({
      id: entry.id,
      userId: entry.user_id,
      jobId: entry.job_id,
      creditsDelta: entry.credits_delta,
      reason: entry.reason,
      createdAt: entry.created_at,
    }));
  } catch (error) {
    console.error("Error in getCreditLedger:", error);
    return [];
  }
}

/**
 * Get paginated credit transaction history for a user.
 * This is a read-only operation with pagination support.
 */
export async function getCreditLedgerPaginated(
  userId: string,
  limit: number = 10,
  offset: number = 0,
): Promise<{ entries: CreditLedgerEntry[]; hasMore: boolean; total: number }> {
  try {
    // First get the total count
    const { count, error: countError } = await (supabase as any)
      .from("credit_ledger")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId);

    if (countError) {
      console.error("Error fetching credit ledger count:", countError);
      return { entries: [], hasMore: false, total: 0 };
    }

    // Query credit_ledger table with offset and limit
    const { data, error } = await (supabase as any)
      .from("credit_ledger")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error("Error fetching credit ledger:", error);
      return { entries: [], hasMore: false, total: count || 0 };
    }

    const entries = (data || []).map((entry: any) => ({
      id: entry.id,
      userId: entry.user_id,
      jobId: entry.job_id,
      creditsDelta: entry.credits_delta,
      reason: entry.reason,
      createdAt: entry.created_at,
    }));

    const hasMore = offset + limit < (count || 0);

    return { entries, hasMore, total: count || 0 };
  } catch (error) {
    console.error("Error in getCreditLedgerPaginated:", error);
    return { entries: [], hasMore: false, total: 0 };
  }
}

/**
 * Consume credits for a completed job via the Supabase Edge Function.
 * This MUST only be called after the job has completed successfully.
 * The user's JWT is forwarded automatically — user_id is never trusted from the client body.
 */
export async function consumeCredits(
  userId: string,
  jobType: JobType,
  jobId: string,
  creditsRequired: number,
): Promise<{
  success: boolean;
  error?: string;
  alreadyProcessed?: boolean;
  new_balance?: number;
}> {
  try {
    const { data: json, error } = await supabase.functions.invoke(
      "consume-credits",
      {
        body: {
          job_id: jobId,
          job_type: jobType,
          credits_required: creditsRequired,
        },
      },
    );

    if (error) {
      // Idempotency: duplicate job_id (already processed)
      if (
        error.message?.includes("23505") ||
        error.message?.includes("duplicate key") ||
        (json as any)?.already_processed
      ) {
        try {
          const balances = await getCreditBalances(userId);
          creditBalanceEvent.dispatchEvent(
            new CustomEvent("updated", {
              detail: { regular: balances.regular, bonus: balances.bonus },
            }),
          );
        } catch {
          // ignore
        }
        return { success: true, alreadyProcessed: true };
      }
      console.error("Failed to consume credits:", error.message);
      return { success: false, error: error.message };
    }

    // Broadcast new balance for immediate UI update
    if (json) {
      try {
        if (
          typeof json.new_regular_balance !== "undefined" ||
          typeof json.new_bonus_balance !== "undefined"
        ) {
          creditBalanceEvent.dispatchEvent(
            new CustomEvent("updated", {
              detail: {
                regular: json.new_regular_balance,
                bonus: json.new_bonus_balance,
              },
            }),
          );
        } else if (json.new_balance != null) {
          creditBalanceEvent.dispatchEvent(
            new CustomEvent("updated", { detail: json.new_balance }),
          );
        }
      } catch {
        // ignore EventTarget errors in unsupported environments
      }

      try {
        if (json.success) emitCreditsConsumed();
      } catch (e) {
        console.error("emitCreditsConsumed failed:", e);
      }
    }

    return {
      success: json?.success ?? false,
      error: json?.error,
      alreadyProcessed: json?.already_processed,
      new_balance: json?.new_balance,
    };
  } catch (err: any) {
    console.error("Error calling consume-credits edge function:", err);
    return { success: false, error: err?.message || String(err) };
  }
}

/**
 * Get all credit costs for display.
 * These costs are defined in /docs/credits.md and must not be changed.
 */
export function getAllCreditCosts(): typeof CREDIT_COSTS {
  return CREDIT_COSTS;
}

/**
 * Subscribe to credit balance changes.
 * Returns an unsubscribe function.
 */
export function subscribeToCreditBalance(
  userId: string,
  onBalanceChange: (newBalances: { regular: number; bonus: number }) => void,
): () => void {
  // Subscribe to changes in the profiles table for this user
  const subscription = supabase
    .channel(`credits:${userId}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "profiles",
        filter: `id=eq.${userId}`,
      },
      (payload) => {
        const newRegular = payload.new.credits_balance ?? 0;
        const newBonus = payload.new.credits_bonus ?? 0;
        onBalanceChange({ regular: newRegular, bonus: newBonus });
      },
    )
    .subscribe();

  // Return unsubscribe function
  return () => {
    supabase.removeChannel(subscription);
  };
}

/**
 * Format a ledger entry for display
 */
export function formatLedgerEntry(entry: CreditLedgerEntry): {
  type: "purchase" | "deduction";
  amount: string;
  description: string;
  date: string;
} {
  const isPurchase = entry.creditsDelta > 0;
  const absAmount = Math.abs(entry.creditsDelta);

  return {
    type: isPurchase ? "purchase" : "deduction",
    amount: isPurchase ? `+${absAmount}` : `-${absAmount}`,
    description: entry.reason,
    date: new Date(entry.createdAt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}
